import React, { useEffect, useRef, useState } from "react";
import { CheckCircle2, AlertCircle } from "lucide-react";
import { adminClient } from "@/api/adminClient";
import { AdminListPager, AdminListToolbar } from "@/components/admin/AdminListToolbar";
import { useAdminListQuery } from "@/components/admin/useAdminListQuery";

export default function AdminSso() {
  const [rows, setRows] = useState([]);
  const [state, setState] = useState("loading");
  const [error, setError] = useState("");
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const generation = useRef(0);
  const mounted = useRef(false);
  const busyRef = useRef(false);
  const actionGeneration = useRef(0);
  const controlsDisabled = state !== "ready" || !!busy;
  const { query, update, reset } = useAdminListQuery();
  async function load() {
    const current = ++generation.current;
    setState("loading");
    setError("");
    try {
      const params = new URLSearchParams({ paged: "true", limit: "25" });
      for (const key of ["q", "verification", "provider_type", "offset"]) if (query[key]) params.set(key, query[key]);
      const result = await adminClient(`/admin/sso?${params}`);
      if (!mounted.current || current !== generation.current) return;
      setRows(result.items || []);
      setTotal(Number(result.total) || 0);
      setState("ready");
    } catch (e) {
      if (!mounted.current || current !== generation.current) return;
      setError(e.message);
      setState("error");
    }
  }
  useEffect(() => {
    mounted.current = true;
    load();
    return () => {
      mounted.current = false;
      generation.current += 1;
      actionGeneration.current += 1;
      busyRef.current = false;
    };
  }, [query.q, query.verification, query.provider_type, query.offset]);
  async function verify(providerId) {
    if (busyRef.current || !mounted.current || state !== "ready") return;
    const action = ++actionGeneration.current;
    busyRef.current = true;
    setBusy(providerId);
    setError("");
    try {
      await adminClient(`/admin/sso/${providerId}/verify-domain`, {
        method: "POST",
      });
      if (!mounted.current || action !== actionGeneration.current) return;
      setConfirm(null);
      await load();
    } catch (e) {
      if (mounted.current && action === actionGeneration.current)
        setError(e.message);
    } finally {
      if (mounted.current && action === actionGeneration.current) {
        busyRef.current = false;
        setBusy(null);
      }
    }
  }
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-foreground">
            SSO providers
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Review organisation identity providers and audited domain
            verification.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={state === "loading" || !!busy}
          className="min-h-11 rounded-lg border border-border px-3 py-2 text-sm font-semibold hover:bg-muted disabled:opacity-50"
        >
          Refresh
        </button>
      </div>
      <AdminListToolbar search={query.q || ""} searchLabel="Search organisation, provider, or domain" total={total} loading={state === "loading"} onSearch={(q) => update({ q })} onReset={reset}>
        <label className="text-sm font-medium">Verification<select aria-label="Filter SSO verification" value={query.verification || "all"} onChange={(event) => update({ verification: event.target.value })} className="mt-1 block min-h-11 rounded-lg border border-input bg-background px-3 py-2 text-sm"><option value="all">All states</option><option value="verified">Verified</option><option value="pending">Not verified</option></select></label>
        <label className="text-sm font-medium">Provider type<select aria-label="Filter SSO provider type" value={query.provider_type || "all"} onChange={(event) => update({ provider_type: event.target.value })} className="mt-1 block min-h-11 rounded-lg border border-input bg-background px-3 py-2 text-sm"><option value="all">All types</option><option value="oidc">OIDC</option><option value="saml">SAML</option></select></label>
      </AdminListToolbar>
      {error && (
        <p
          className="rounded-lg border border-destructive/40 bg-card p-4 text-sm text-destructive"
          role="alert"
        >
          {error}{" "}
          <button type="button" onClick={load} className="ml-2 underline">
            Retry
          </button>
        </p>
      )}
      {state === "loading" && (
        <p
          role="status"
          className="rounded-lg border border-border bg-card p-5 text-sm text-muted-foreground"
        >
          Loading SSO providers…
        </p>
      )}
      {state === "ready" && rows.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground">
          No organisation has registered an SSO provider yet.
        </p>
      ) : (
        rows.length > 0 &&
        state !== "idle" && (
          <div
            role="region"
            aria-label="SSO providers table"
            tabIndex={0}
            className="overflow-x-auto rounded-xl border border-border bg-card shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
          >
            <table className="w-full min-w-[700px] text-left text-sm">
              <caption className="sr-only">Organisation SSO providers</caption>
              <thead>
                <tr className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-3">Organisation</th>
                  <th className="px-4 py-3">Provider</th>
                  <th className="px-4 py-3">Domain</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    className="border-b border-border last:border-0"
                  >
                    <th scope="row" className="px-4 py-3 font-semibold">
                      {row.organisation || "—"}
                    </th>
                    <td className="px-4 py-3">
                      {row.displayName || row.providerId}
                    </td>
                    <td className="px-4 py-3">
                      <code className="text-xs">{row.domain}</code>
                    </td>
                    <td className="px-4 py-3">
                      {row.domainVerified ? (
                        <span className="inline-flex items-center gap-1 rounded-full border border-teal-200 bg-teal-50 px-2 py-1 text-xs font-semibold text-teal-800">
                          <CheckCircle2
                            className="h-3 w-3"
                            aria-hidden="true"
                          />
                          Verified
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-800">
                          <AlertCircle className="h-3 w-3" aria-hidden="true" />
                          Not verified
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {!row.domainVerified &&
                        (confirm === row.providerId ? (
                          <span className="inline-flex flex-wrap items-center justify-end gap-2">
                            <span className="text-xs text-muted-foreground">
                              Mark {row.domain} verified?
                            </span>
                            <button
                              type="button"
                              onClick={() => verify(row.providerId)}
                              disabled={controlsDisabled}
                              className="min-h-11 rounded-lg bg-teal-700 px-3 py-2 text-xs font-semibold text-white hover:bg-teal-800 disabled:opacity-50"
                            >
                              {busy === row.providerId
                                ? "Verifying…"
                                : "Confirm"}
                            </button>
                            <button
                              type="button"
                              onClick={() => setConfirm(null)}
                              disabled={controlsDisabled}
                              className="min-h-11 rounded-lg border border-border px-3 py-2 text-xs font-semibold hover:bg-muted disabled:opacity-50"
                            >
                              Cancel
                            </button>
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setConfirm(row.providerId)}
                            disabled={controlsDisabled}
                            className="min-h-11 rounded-lg border border-border px-3 py-2 text-xs font-semibold hover:bg-muted disabled:opacity-50"
                          >
                            Mark verified
                          </button>
                        ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
      <AdminListPager offset={Number(query.offset || 0)} limit={25} total={total} disabled={controlsDisabled} onPage={(offset) => update({ offset }, { resetOffset: false })} />
      <p className="text-xs text-muted-foreground">
        Manual overrides are for customers who have proven domain ownership out
        of band and are recorded as <code>sso.domain.verified_by_provider</code>
        .
      </p>
    </div>
  );
}
